import type { CharacterImageKind } from "./types";

export const MAX_CHARACTER_IMAGE_BYTES = 10 * 1024 * 1024;

const CHARACTER_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface CharacterImageDraft extends ImageDimensions {
  file: File;
  previewUrl: string;
}

export type ImageDimensionDecoder = (file: File) => Promise<ImageDimensions>;

export async function decodeImageDimensions(file: File): Promise<ImageDimensions> {
  const bitmap = await createImageBitmap(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

export function hasExpectedCharacterImageRatio(kind: CharacterImageKind, width: number, height: number): boolean {
  const expectedWidth = kind === "portrait" ? height * 9 / 21 : height;
  return Math.abs(width - expectedWidth) <= 1;
}

export interface InspectedCharacterImage extends ImageDimensions {
  needsCrop: boolean;
}

export async function inspectCharacterImageFile(
  file: File,
  kind: CharacterImageKind,
  decode: ImageDimensionDecoder = decodeImageDimensions,
): Promise<InspectedCharacterImage> {
  if (!CHARACTER_IMAGE_TYPES.has(file.type)) throw new Error("character_image_type_invalid");
  if (file.size <= 0) throw new Error("character_image_empty");
  if (file.size > MAX_CHARACTER_IMAGE_BYTES) throw new Error("character_image_too_large");
  const dimensions = await decode(file);
  if (!Number.isInteger(dimensions.width) || dimensions.width <= 0
    || !Number.isInteger(dimensions.height) || dimensions.height <= 0) {
    throw new Error("character_image_dimensions_invalid");
  }
  return { ...dimensions, needsCrop: !hasExpectedCharacterImageRatio(kind, dimensions.width, dimensions.height) };
}

export async function validateCharacterImageFile(
  file: File,
  kind: CharacterImageKind,
  decode: ImageDimensionDecoder = decodeImageDimensions,
): Promise<ImageDimensions> {
  const { needsCrop, ...dimensions } = await inspectCharacterImageFile(file, kind, decode);
  if (needsCrop) throw new Error("character_image_ratio_invalid");
  return dimensions;
}

export function revokeImageDraft(draft: CharacterImageDraft | null): void {
  if (draft) URL.revokeObjectURL(draft.previewUrl);
}
